import { AlertTriangle, Combine, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CandidateGroupCard } from "@/components/meta-orders/candidate-group-card";
import { MetaOrderCard } from "@/components/meta-orders/meta-order-card";
import { useMetaCandidates, useMetaOrders } from "@/hooks/use-meta-orders";
import { fulfillmentMergeRoute } from "@/lib/router";
import { parseMetaNumber } from "@/lib/meta-orders/number";
import { plural } from "@/lib/utils";

function SectionTitle({ children, count }: { children: React.ReactNode; count?: number }) {
  return (
    <div className="flex items-baseline gap-2">
      <h3 className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">{children}</h3>
      {count !== undefined && <span className="text-xs text-muted-foreground tabular-nums">{count}</span>}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="ring-1 ring-foreground/10 px-3 py-6 text-center text-xs text-muted-foreground">{children}</div>;
}

/** Spajanje neposlanih narudžbi istog kupca (kroz sve shopove) u jednu meta narudžbu. */
export function MergeShipmentsPage() {
  const { group: focusGroup, meta: focusMeta } = fulfillmentMergeRoute.useSearch();
  const candidates = useMetaCandidates();
  const metas = useMetaOrders();
  const focusMetaId = focusMeta ? (/^\d+$/.test(focusMeta) ? Number(focusMeta) : parseMetaNumber(focusMeta)) : null;

  const groups = candidates.data?.groups ?? [];
  const skipped = candidates.data?.skippedStores ?? [];
  const activeMetas = metas.data ?? [];
  const refreshing = candidates.isFetching || metas.isFetching;
  const groupMissing = !!focusGroup && candidates.isSuccess && !groups.some((g) => g.id === focusGroup);

  return (
    <div className="p-4 flex flex-col gap-4 max-w-5xl">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Combine className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-medium">Spajanje pošiljki</h2>
          {candidates.isSuccess && (
            <span className="text-xs text-muted-foreground">
              · {groups.length} {plural(groups.length, "kupac", "kupca", "kupaca")} s više neposlanih narudžbi
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={refreshing}
          onClick={() => {
            void candidates.refetch();
            void metas.refetch();
          }}
        >
          <RefreshCw className={refreshing ? "animate-spin" : undefined} />
          Osvježi
        </Button>
      </div>

      {skipped.length > 0 && (
        <div className="flex gap-3 items-start bg-yellow-500/10 border border-yellow-500/20 p-3">
          <AlertTriangle className="h-4 w-4 text-yellow-600 shrink-0 mt-0.5" />
          <p className="text-xs text-yellow-700">
            Shopovi koji nisu u webhook modu nisu uključeni: {skipped.map((s) => s.name).join(", ")}. Lista kandidata za
            njih nije potpuna.
          </p>
        </div>
      )}

      <section className="flex flex-col gap-2.5">
        <SectionTitle count={candidates.isSuccess ? groups.length : undefined}>Kandidati</SectionTitle>
        {groupMissing && (
          <p className="text-xs text-muted-foreground">
            Tražena grupa više nije kandidat — narudžbe su možda već spojene, poslane ili promijenile status.
          </p>
        )}
        {candidates.isLoading ? (
          <Empty><Loader2 className="inline size-4 animate-spin" /></Empty>
        ) : candidates.isError ? (
          <Empty><span className="text-red-600">{candidates.error.message}</span></Empty>
        ) : groups.length === 0 ? (
          <Empty>Nema kupaca s više neposlanih narudžbi.</Empty>
        ) : (
          groups.map((g) => <CandidateGroupCard key={g.id} group={g} highlighted={g.id === focusGroup} />)
        )}
      </section>

      <section className="flex flex-col gap-2.5">
        <SectionTitle count={metas.isSuccess ? activeMetas.length : undefined}>Spojene pošiljke</SectionTitle>
        {metas.isLoading ? (
          <Empty><Loader2 className="inline size-4 animate-spin" /></Empty>
        ) : metas.isError ? (
          <Empty><span className="text-red-600">{metas.error.message}</span></Empty>
        ) : activeMetas.length === 0 ? (
          <Empty>Nema aktivnih spojenih pošiljki.</Empty>
        ) : (
          activeMetas.map((m) => <MetaOrderCard key={m.id} meta={m} highlighted={m.id === focusMetaId} />)
        )}
      </section>
    </div>
  );
}
